import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { _getFile } from '@/api/files/queries';
import { Dropzone, FileItem, FileItemFrame, Label, theme, Typography } from '@/design-system';
import { useGlobalModal } from '@/global-hooks/use-global-modal';
import { COVER_IMAGE_ID } from '@/shared/constants/fund-documents';
import { useEntitySlice } from '@/stores/selectors';
import { isImageFile } from '@/utils/validate-file-type';

import { LogoUploadColumn } from '../styled';
import { useCreateEntity } from '../use-create-entity';
import { ENTITY_FIELD_ACCESS, useEntityFieldAccess } from '../use-entity-field-access';
import { useEntityReviewMode } from '../use-entity-review-mode';

const LOGO_FIELD = 'logoImageId';

export const LogoUpload = () => {
  const param = useParams();
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { updateStepForm, stepForms, isLogoUploading } = useEntitySlice();
  const { uploadFile } = useCreateEntity();
  const { accessOf, deniedReasonFor } = useEntityFieldAccess();
  const { forms, isChanged, highlightIf } = useEntityReviewMode();
  const isDenied = accessOf(LOGO_FIELD) === ENTITY_FIELD_ACCESS.denied;
  const deniedReason = deniedReasonFor(LOGO_FIELD);
  const cover = forms.logoImageId ?? COVER_IMAGE_ID;

  const { data: currentFile, isLoading: isFileLoading } = useQuery(_getFile(cover));

  const localPreviewUrl = useMemo(() => {
    if (stepForms.previewLogo instanceof File) {
      return URL.createObjectURL(stepForms.previewLogo);
    }
    return null;
  }, [stepForms.previewLogo]);

  useEffect(() => {
    return () => {
      if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl);
    };
  }, [localPreviewUrl]);

  const handlePick = async (file: File) => {
    if (!param.id) {
      updateStepForm({ previewLogo: file });
    } else {
      const fileId = await uploadFile(file, param.id);
      updateStepForm({ logoImageId: fileId, previewLogo: null });
    }
  };

  const { handleOpen } = useGlobalModal('CUSTOMIZE_IMG_MODAL', {
    inputFile: pendingFile ?? undefined,
    outputWidth: 512,
    outputHeight: 512,
    onSave: (newFile: File) => handlePick(newFile),
  });

  const acceptImage = async (files: File[]) => {
    if (isDenied) return;
    for (const file of files) {
      if (await isImageFile(file)) {
        setPendingFile(file);
        break;
      }
    }
  };

  const onFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const input = e.currentTarget;
    if (file) await acceptImage([file]);
    if (input) input.value = '';
  };

  const displayUrl = localPreviewUrl ?? currentFile?.url ?? null;

  useEffect(() => {
    if (!pendingFile) return;
    handleOpen();
    setPendingFile(null);
  }, [pendingFile, handleOpen]);

  const renderUploadArea = () => {
    if (!displayUrl && !isFileLoading && !isLogoUploading) {
      return (
        <Dropzone
          subject="logo"
          accept="image/*"
          acceptLabel="PNG, JPG, SVG"
          disabled={isDenied}
          onFilesSelected={acceptImage}
        />
      );
    }

    const localLogo = stepForms.previewLogo instanceof File ? stepForms.previewLogo : null;
    const isBusy = isFileLoading || isLogoUploading;

    return (
      <FileItemFrame changed={Boolean(highlightIf(isChanged(LOGO_FIELD)))}>
        <FileItem
          variant="image"
          file={{
            id: localLogo ? 'local-logo' : cover,
            name: localLogo?.name ?? currentFile?.fileName ?? 'Entity logo',
            size: localLogo?.size ?? currentFile?.size,
            contentType: localLogo?.type ?? currentFile?.contentType,
            previewUrl: displayUrl ?? undefined,
            status: isBusy ? 'uploading' : 'uploaded',
            progress: null,
          }}
          onReplace={isDenied ? undefined : () => fileInputRef.current?.click()}
        />
      </FileItemFrame>
    );
  };

  return (
    <LogoUploadColumn>
      <Label optional>Upload entity logo</Label>

      {deniedReason && (
        <Typography
          variant="body3"
          color={theme.colors.text.secondary}
          data-testid="logo-upload-denied"
        >
          {deniedReason}
        </Typography>
      )}

      <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={onFileInputChange} />

      {renderUploadArea()}
    </LogoUploadColumn>
  );
};
